import type { Chart, JourneyMap, Geography, ContentNode, DeckState, Slide, SlideStyle } from "./types";
import { geographies } from "./geography";
import { renderCustom } from "./custom";
import { element, escapeHTML } from "./dom";
import { PassageHistory, PassageThread, Scripture } from "./primitives";
export { element } from "./dom";
const svgText = (text: string, x: number, y: number, cls = "ss-chart-axis") =>
  '<text class="' + cls + '" x="' + x + '" y="' + y + '">' + escapeHTML(text) + '</text>';

export function chartMarkup(data: Chart): string {
  const values = data.points.map(p => p.value);
  const low = Math.min(0, ...values), high = Math.max(1, ...values);
  const y = (v: number) => 340 - (v - low) / (high - low) * 280;
  const x = (i: number) => 90 + i * 760 / Math.max(1, values.length - 1);
  const total = Math.max(1, values.reduce((a, v) => a + Math.max(0, v), 0));
  let offset = 0, marks = "";
  data.points.forEach((point, i) => {
    let mark = "";
    const width = 780 / values.length, bx = 80 + i * width, zero = y(0);
    switch (data.chartType) {
      case "bar":
        mark = '<rect class="ss-chart-bar" x="' + (bx + width * .15) + '" y="' + Math.min(zero, y(point.value)) +
          '" width="' + width * .7 + '" height="' + Math.abs(zero - y(point.value)) + '"/>';
        break;
      case "line": case "area": case "sparkline":
        if (i > 0) {
          const segment = x(i - 1) + "," + y(values[i - 1]) + " " + x(i) + "," + y(point.value);
          if (data.chartType === "area") mark += '<polygon class="ss-chart-area" points="' +
            x(i - 1) + "," + zero + " " + segment + " " + x(i) + "," + zero + '"/>';
          mark += '<polyline class="ss-chart-line ss-draw" pathLength="1" points="' + segment + '"/>';
        }
        mark += '<circle class="ss-chart-point" cx="' + x(i) + '" cy="' + y(point.value) + '" r="6"/>';
        break;
      case "donut": {
        const portion = Math.max(0, point.value) / total;
        mark = '<circle class="ss-chart-donut ss-draw" cx="480" cy="205" r="116" pathLength="1" style="stroke:var(--chart-' +
          (i % 4 + 2) + ')" stroke-dasharray="' + portion + " " + (1 - portion) + '" stroke-dashoffset="' + -offset + '"/>';
        offset += portion; break;
      }
      case "progress": {
        const part = Math.max(0, point.value) / total;
        mark = '<rect class="ss-chart-progress" x="' + (80 + offset * 780) + '" y="180" width="' +
          part * 780 + '" height="44" style="fill:var(--chart-' + (i % 4 + 2) + ')"/>';
        offset += part; break;
      }
    }
    const line = ["line", "area", "sparkline"].includes(data.chartType);
    const radial = ["donut", "progress"].includes(data.chartType);
    const labelX = line ? x(i) : bx + width / 2;
    marks += '<g data-ss-id="' + escapeHTML(data.id + "-item-" + i) + '" role="img" aria-label="' +
      escapeHTML(point.label + ": " + point.value) + '">' + mark +
      svgText(point.label, labelX, 388) + svgText(String(point.value), labelX, radial ? 365 : y(point.value) - 12, "ss-chart-value") + "</g>";
  });
  return '<figure class="ss-chart ss-chart-' + data.chartType + '" aria-label="' + escapeHTML(data.title) +
    '"><svg viewBox="0 0 960 430" role="img"><title>' + escapeHTML(data.title) + '</title>' +
    '<line class="ss-chart-axis-line" x1="70" y1="' + y(0) + '" x2="890" y2="' + y(0) + '"/>' +
    marks + '</svg><figcaption>' + escapeHTML(data.title) + "</figcaption></figure>";
}

export function journeyMarkup(data: JourneyMap, map: Geography): string {
  const positions = data.stops.map(stop => {
    const point = map.cities[stop.city];
    if (!point) throw new Error("Unknown city " + stop.city + " in map " + data.map);
    return point;
  });
  let svg = map.land ? '<path class="ss-map-land" d="' + escapeHTML(map.land) + '"/>' : "";
  for (const region of map.regions ?? []) svg += svgText(region.label, region.x, region.y, "ss-chart-label");
  positions.forEach(([x, y], i) => {
    if (i > 0) svg += '<path class="ss-route ss-draw" data-stop="' + i + '" pathLength="1" d="M' +
      positions[i - 1].join(" ") + " L" + x + " " + y + '"/>';
    svg += '<g data-city="' + i + '"><circle class="ss-city" cx="' + x + '" cy="' + y +
      '" r="9"/>' + svgText(data.stops[i].city, x, y + 30) + "</g>";
  });
  return '<figure class="ss-journey"><svg viewBox="' + escapeHTML(map.viewBox) + '" role="img" aria-label="' +
    escapeHTML(data.title) + '"><title>' + escapeHTML(data.title + ". " + map.label) + '</title>' + svg +
    '</svg><figcaption>' + escapeHTML(data.title) + '</figcaption><p class="ss-map-notice">' +
    escapeHTML(map.notice ?? "") + '</p><p class="ss-stop-caption" aria-live="polite"></p><ol class="ss-journey-rail">' +
    data.stops.map(s => "<li>" + escapeHTML(s.city) + "</li>").join("") + "</ol></figure>";
}
export function mountComponent(node: ContentNode, host: HTMLElement) {
  switch (node.kind) {
    case "chart": host.innerHTML = chartMarkup(node); break;
    case "journey": {
      const map = geographies[node.map]; if (!map) throw new Error("Unknown geography: " + node.map);
      host.innerHTML = journeyMarkup(node, map); break;
    }
    case "timeline": case "diagram":
      host.innerHTML = '<div class="ss-diagram ss-' + node.kind + '"><ol>' +
        node.items.map(item => "<li>" + escapeHTML(item) + "</li>").join("") + "</ol></div>"; break;
    case "custom": break;
  }
}
export function updateComponent(node: ContentNode, host: HTMLElement, state: DeckState) {
  if (node.kind === "custom") { renderCustom(node.name, host, node.props, state); return; }
  if (node.kind !== "journey") return;
  const stopIndex = state.componentState[node.id]?.stopIndex ?? node.stops.length - 1;
  host.querySelectorAll<SVGElement>("[data-stop]").forEach(path => {
    path.classList.toggle("ss-route-hidden", Number(path.dataset.stop) > stopIndex);
  });
  host.querySelectorAll<SVGElement>("[data-city]").forEach(city => {
    const i = Number(city.dataset.city);
    city.classList.toggle("ss-city-future", i > stopIndex);
    city.classList.toggle("ss-city-past", i < stopIndex);
    city.classList.toggle("ss-city-current", i === stopIndex);
  });
  const stop = node.stops[stopIndex];
  host.querySelector(".ss-stop-caption")!.textContent = stop ? stop.city + " · " + stop.passage + " — " + stop.summary : "Press next to begin the journey";
  const rail = host.querySelector<HTMLOListElement>(".ss-journey-rail")!;
  rail.hidden = !node.rail;
  [...rail.children].forEach((item, i) => {
    (item as HTMLElement).hidden = i > stopIndex;
    item.classList.toggle("is-current", i === stopIndex);
  });
}
export function renderPassages(host: HTMLElement, slide: Slide, state: DeckState) {
  // Authored passage chains are slide footers, outside the bounded text region.
  host.querySelectorAll(".lesson-passage-chain").forEach(chain => host.append(chain));
  host.querySelector(".ss-passages")?.remove();
  const container = element("aside", "ss-passages");
  container.dataset.ssId = "passages";
  if (slide.passage) container.append(Scripture(slide.passage));
  const history = state.passageHistory.filter(p => p.reference !== slide.passage);
  if (slide.history !== "none" && history.length) container.append(PassageHistory(history, slide.history ?? "rail"));
  const thread = slide.thread && state.activePassageThreads[slide.thread];
  if (thread) container.append(PassageThread(thread, slide.hideFuture));
  if (container.children.length) host.append(container);
}
export function applyStyle(host: HTMLElement, style: SlideStyle) {
  for (const [id, fields] of Object.entries(style)) {
    const target = host.querySelector<HTMLElement>('[data-ss-id="' + CSS.escape(id) + '"]');
    if (!target) continue;
    for (const [key, value] of Object.entries(fields)) {
      const cssKey = ({ x: "left", y: "top", align: "text-align" } as Record<string, string>)[key] ??
        key.replace(/[A-Z]/g, c => "-" + c.toLowerCase());
      const length = typeof value === "number" ? (value / 19.2) + "cqw" : value;
      target.style.setProperty(cssKey, length);
    }
    if ((fields.x !== undefined || fields.y !== undefined) && !fields.position) target.style.position = "relative";
  }
}
